/**
 * The pure half of the public schedule read (`eventModelV2.getPublicSchedule`): one stored schedule row
 * to one public match, and the per-court summary. No I/O; the model reads the rows and passes in the
 * live blobs, seeding, team entries and name order it loaded beside them.
 */
import { projectSchedulePlayers } from '@/app/[locale]/event/[eventId]/[slug]/(tabs)/schedule/libs/matchPlayerProjection';
import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { courtDelayMinutes } from '@/lib/schedule/courtDelay';
import { teamEntryKey, type TeamEntry } from './teamLabels';
import { venueOfCourt, type EventVenueContext } from './venues';

type SchedulePlayer = Parameters<typeof projectSchedulePlayers>[0][number]['player'];

type DrawMatchEmbed = {
  draw_match_id?: string | null;
  draw_id?: string | null;
  draw?: { hidden_from_public?: boolean | null } | null;
  stage?: { name?: string | null } | null;
  round?: { name?: string | null } | null;
};

/** The columns and embeds of one `event_match_schedule` row as the public read selects them. */
export type ScheduleSourceRow = {
  id: string;
  court_id: string | null;
  scheduled_datetime: string | null;
  estimated_duration_minutes: number | null;
  is_walkover: boolean | null;
  outcome: string | null;
  player_1_id: string | null;
  player_2_id: string | null;
  player1: unknown;
  player2: unknown;
  event_group: { id?: string | null; name?: string | null; sort_order?: number | null } | null;
  match: {
    id?: string | null;
    status?: string | null;
    winner_id?: string | null;
    retired_player_id?: string | null;
    player_1_games?: number | null;
    player_2_games?: number | null;
    match_game?: {
      game_number: number;
      player_1_score: number;
      player_2_score: number;
      status?: string | null;
    }[];
  } | null;
  draw_match: DrawMatchEmbed | DrawMatchEmbed[] | null;
};

export type ScheduleProjectionInputs = {
  /** `match.app_state` for in-progress matches, by match id. */
  liveBlobs: Map<string, unknown>;
  /** Seeding group by `${draw_id}:${player_id}`. */
  seedByDrawPlayer: Map<string, string>;
  /** Team entries by `teamEntryKey(event_group_id, player_id)`. */
  teamEntries: Map<string, TeamEntry>;
  isJapanese: boolean;
};

export function firstDrawMatch(r: Pick<ScheduleSourceRow, 'draw_match'>): DrawMatchEmbed | null {
  return (Array.isArray(r.draw_match) ? r.draw_match[0] : r.draw_match) ?? null;
}

// Status trusts the explicit signals only: status='completed' OR winner_id set. An older rule demoted
// `in_progress` matches scheduled >3h ago, which wrongly demoted genuinely long matches.
export function projectScheduleRows(
  rows: readonly ScheduleSourceRow[],
  { liveBlobs, seedByDrawPlayer, teamEntries, isJapanese }: ScheduleProjectionInputs,
) {
  return rows.map((r) => {
    const matchStatus = r.match?.status;
    const isCompleted = matchStatus === 'completed' || !!r.match?.winner_id;
    const isLive = matchStatus === 'in_progress' && !isCompleted;
    const drawMatch = firstDrawMatch(r);
    const stageName = drawMatch?.stage?.name ?? '';
    const matchRecord = r.match;
    const eventGroup = r.event_group;
    // The games columns are written at finalize only. While live, the truth is the scorer blob, read
    // through the shared resolver (games won so far + the current game's points), falling back to the
    // columns when it does not resolve. Only numbers leave here; the blob itself never does.
    const live =
      isLive && matchRecord?.id ? tryResolveAppState(liveBlobs.get(matchRecord.id)) : null;
    const p1Games = live ? live.player_1_games : (matchRecord?.player_1_games ?? null);
    const p2Games = live ? live.player_2_games : (matchRecord?.player_2_games ?? null);
    const score = p1Games !== null && p2Games !== null ? { p1: p1Games, p2: p2Games } : null;
    const liveGame = live?.current_game
      ? { p1: live.current_game.player_1_score, p2: live.current_game.player_2_score }
      : null;
    // Same rule as the admin live-schedule card: the blob while live, the match_game rows once
    // finalised, games with no point yet dropped.
    const rawGames = live
      ? live.games
      : [...(matchRecord?.match_game ?? [])].sort((a, b) => a.game_number - b.game_number);
    const games = rawGames
      .filter((g) => g.player_1_score > 0 || g.player_2_score > 0)
      .map((g) => ({
        p1: g.player_1_score,
        p2: g.player_2_score,
        // A retired match's unfinished game: shown as played, never counted won.
        ...('status' in g && g.status === 'in_progress' ? { partial: true } : {}),
      }));
    const drawId = drawMatch?.draw_id ?? null;
    const seedOf = (pid: string | null) =>
      drawId && pid ? (seedByDrawPlayer.get(`${drawId}:${pid}`) ?? null) : null;
    const groupId = eventGroup?.id ?? null;
    const p1Id = r.player_1_id;
    const p2Id = r.player_2_id;
    const { players, rosters } = projectSchedulePlayers(
      [
        {
          player: r.player1 as SchedulePlayer,
          entry: groupId && p1Id ? teamEntries.get(teamEntryKey(groupId, p1Id)) : undefined,
        },
        {
          player: r.player2 as SchedulePlayer,
          entry: groupId && p2Id ? teamEntries.get(teamEntryKey(groupId, p2Id)) : undefined,
        },
      ],
      isJapanese,
    );
    return {
      id: r.id,
      matchId: matchRecord?.id ?? null,
      courtId: r.court_id ?? '',
      scheduledAt: r.scheduled_datetime as string,
      durationEstimateMin: r.estimated_duration_minutes ?? 60,
      stage: stageName || '',
      division: eventGroup?.name ?? null,
      divisionSortOrder: eventGroup ? (eventGroup.sort_order ?? 0) : null,
      players,
      playerIds: [p1Id, p2Id],
      divisionId: groupId,
      rosters,
      score,
      liveGame,
      games,
      code: drawMatch?.draw_match_id ?? null,
      roundName: drawMatch?.round?.name || null,
      seeds: [seedOf(p1Id), seedOf(p2Id)],
      isWalkover: !!r.is_walkover,
      outcome: r.outcome ?? null,
      status: (isCompleted ? 'completed' : isLive ? 'live' : 'upcoming') as
        | 'completed'
        | 'live'
        | 'upcoming',
      winnerId: matchRecord?.winner_id ?? null,
      retiredPlayerId: matchRecord?.retired_player_id ?? null,
    };
  });
}

export type PublicScheduleMatch = ReturnType<typeof projectScheduleRows>[number];

/**
 * Per-court summary, the web schedule page's rule (`courtDelayMinutes`) so the app's `delay_min` and
 * the website agree. A court is late only once its live match is still on court past the time the NEXT
 * match there was due. Only when the day viewed is today (a past or future day has no "now", so 0),
 * capped inside `courtDelayMinutes`. The live match itself is always surfaced.
 */
export function projectScheduleCourts(
  courtDefs: readonly CourtDefinition[],
  matches: readonly PublicScheduleMatch[],
  venueCtx: EventVenueContext,
  { viewingToday, nowMs }: { viewingToday: boolean; nowMs: number },
) {
  return courtDefs.map((c) => {
    const onCourt = matches.filter((m) => m.courtId === c.id);
    const liveCandidate = onCourt.find((m) => m.status === 'live');
    const delayMin = viewingToday ? (courtDelayMinutes(onCourt, nowMs) ?? 0) : 0;
    return {
      id: c.id,
      name: c.name,
      label: c.description,
      delayMin,
      liveMatchId: liveCandidate?.id ?? null,
      venueId: venueOfCourt(venueCtx, c.id)?.id ?? null,
    };
  });
}
